import { AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output, inject, DestroyRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, take } from 'rxjs';
import * as _ from 'lodash-es';
import { environment } from '../../../environments/environment';
import { AppService } from '../../app.service';
import { GlobalService } from '../../global/services/global.service';
import { GridFactory } from '../../core/grid/services/grid.service';
import { GridSaveFactoryService } from '../../core/grid/services/grid-save-factory.service';
import { GRID_SCAFFOLD_HTML } from '../../core/grid/grid-scaffold';
import { AdminGridCacheService } from './admin-grid-cache.service';
import { AdminScreenContextService } from './admin-screen-context.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';

declare var $: any;

export interface AdminGridConfig {
  /** Numeric action_id (used in loadGridJson?action_id=...). Provide either actionId or actionCode. */
  actionId?: number;
  /** String action_code (used in loadGridJson?action_code=...). Takes precedence over actionId when set. */
  actionCode?: string;
  gridId: string;
  /** Extra params merged into every loadGridData call (e.g. client_key). */
  extraParams?: Record<string, any>;
  /** If true, data is loaded automatically after grid init. Default: true. */
  autoLoad?: boolean;
  /**
   * Cache key used by AdminGridCacheService.
   * When set, the directive caches data responses and restores them on re-init.
   * Typically set to the route path (e.g. 'admin/users-list').
   */
  cacheKey?: string;
  /** Legacy `#screen_key` hidden span value used by exportToExcel. */
  exportScreenKey?: number | string;
  /** When export uses a different action_id than loadGridJson. */
  exportActionId?: number | string;
  /** When export uses a different grid_id than loadGridJson. */
  exportGridId?: number | string;
}

export interface AdminGridCallbackEvent {
  callName: string;
  data: any;
  type: string;
}

/**
 * Reusable gizmoGrid directive for admin screens.
 *
 * Replaces the boilerplate that every admin component copy-pastes:
 *   - GRID_SCAFFOLD_HTML injection
 *   - jQuery gizmoGrid initialization
 *   - loadGridData → processAndLoadData pipeline
 *   - processTemplates (header parse, column template default)
 *   - columnTemplate save callback → GridSaveFactoryService
 *   - destroy lifecycle
 *   - data caching via AdminGridCacheService
 *
 * Usage in a template:
 *   <div adminGrid [gridConfig]="gridCfg" (gridCallback)="onCallback($event)"></div>
 *
 * The host component only needs to:
 *   1. Provide an AdminGridConfig object
 *   2. Handle custom callbacks (row click, form, etc.)
 *   3. Call loadData() / loadDataWithParams() when filters change
 */
@Directive({
    selector: '[adminGrid]',
    exportAs: 'adminGrid',
    standalone: false
})
export class AdminGridDirective implements AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Input() gridConfig!: AdminGridConfig;

  /** Fires for every gizmoGrid callback. */
  @Output() gridCallback = new EventEmitter<AdminGridCallbackEvent>();

  /** Fires after processAndLoadData completes (both fresh and cached). */
  @Output() gridDataLoaded = new EventEmitter<any>();

  /** Emits true when data fetch starts, false when it completes or fails. */
  @Output() loadingStateChange = new EventEmitter<boolean>();

  private ggApi: any = null;
  private loadSub?: Subscription;
  private gridInitialized = false;
  private lastLoadedData: any = null;
  private alertObserver: MutationObserver | null = null;

  constructor(
    private el: ElementRef,
    private http: HttpClient,
    private appService: AppService,
    private globalService: GlobalService,
    private gridFactory: GridFactory,
    private gridSaveFactory: GridSaveFactoryService,
    private cacheService: AdminGridCacheService,
    private adminScreenContext: AdminScreenContextService,
    private gtwSnackbarService: GtwSnackbarService,
  ) {}

  /** Expose the raw gizmoGrid API for advanced use. */
  get api(): any {
    return this.ggApi;
  }

  get isInitialized(): boolean {
    return this.gridInitialized;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngAfterViewInit(): void {
    setTimeout(() => this.initGrid(), 0);
  }

  ngOnDestroy(): void {
    this.destroyGrid();
  }

  // ---------------------------------------------------------------------------
  // Public API — callable from the host component via template reference
  // ---------------------------------------------------------------------------

  /**
   * Load (or reload) grid data using the current gridConfig.
   * Optionally pass extra params that override gridConfig.extraParams
   * for this single call.
   */
  loadData(overrideParams?: Record<string, any>): void {
    this.fetchAndLoad(overrideParams, false);
  }

  /** Force reload — ignores cache. */
  refresh(overrideParams?: Record<string, any>): void {
    this.fetchAndLoad(overrideParams, true);
  }

  /** Clear the cache entry for this grid (if cacheKey is set). */
  clearCache(): void {
    if (this.gridConfig?.cacheKey) {
      this.cacheService.clear(this.gridConfig.cacheKey);
    }
  }

  showLoader(): void {
    this.ggApi?.showLoader?.();
  }

  hideLoader(): void {
    this.ggApi?.hideLoader?.();
  }

  // ---------------------------------------------------------------------------
  // Grid initialization
  // ---------------------------------------------------------------------------

  private initGrid(): void {
    if (typeof $ === 'undefined' || typeof $.fn?.gizmoGrid !== 'function') {
      console.warn('[AdminGrid] jQuery or gizmoGrid plugin not available');
      return;
    }

    const el = this.el.nativeElement;
    if (!el) return;

    const $grid = $(el);
    $grid.html(GRID_SCAFFOLD_HTML);

    $grid.gizmoGrid({
      fixedColumn: 2,
      gridHeight: 'auto',
      defaultRowHeight: 35,
      callBack: (obj: any) => this.onGridCallback(obj),
    });

    this.ggApi = $grid.data('gizmoGridApi');
    this.gridInitialized = true;

    this.setupAlertObserver();

    $grid.on('mousewheel.ggMultiSelect DOMMouseScroll.ggMultiSelect', '.gg-multi-select', (event: any) => {
      event.stopPropagation();
    });

    $grid.on('click.filteredExportSync', '.tools-export', () => {
      this.syncFilteredExportVisibility();
    });

    $grid.on('click.exportAction', '.gg-export-action', (event: any) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const action = $(event.currentTarget).attr('action');
      if (action === 'filtered-export') {
        this.exportToExcelFiltered();
      } else {
        this.exportToExcel();
      }
    });

    this.syncFilteredExportVisibility();

    const autoLoad = this.gridConfig?.autoLoad !== false;
    if (autoLoad) {
      this.fetchAndLoad(undefined, false);
    }
  }

  private destroyGrid(): void {
    this.alertObserver?.disconnect();
    this.alertObserver = null;
    if (this.loadSub) {
      this.loadSub.unsubscribe();
      this.loadSub = undefined;
    }
    const $grid = $(this.el.nativeElement);
    if ($grid) {
      $grid.off('mousewheel.ggMultiSelect DOMMouseScroll.ggMultiSelect');
      $grid.off('click.exportAction');
      $grid.off('click.filteredExportSync');
    }
    if (this.ggApi?.destroy) {
      this.ggApi.destroy();
      this.ggApi = null;
    }
    this.gridInitialized = false;
    this.adminScreenContext.setGridData(null);
  }

  private setupAlertObserver(): void {
    const container: HTMLElement | null =
      this.el.nativeElement?.querySelector('.gg-alert-container');
    if (!container) return;

    this.alertObserver?.disconnect();
    this.alertObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (!(node instanceof HTMLElement) || !node.classList.contains('gg-alert')) continue;

          const message = node.querySelector('.gg-alert-message')?.textContent?.trim() || '';
          node.remove();

          if (message) {
            const panelClass =
              node.classList.contains('alert-error') || node.classList.contains('alert-danger')
                ? 'gtw-snackbar--red'
                : node.classList.contains('alert-success')
                  ? 'gtw-snackbar--green'
                  : node.classList.contains('alert-info')
                    ? 'gtw-snackbar--yellow'
                    : 'gtw-snackbar--orange';

            this.gtwSnackbarService.openSnackBar(message, panelClass);
          }
        }
      }
    });

    this.alertObserver.observe(container, { childList: true });
  }

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  private fetchAndLoad(overrideParams?: Record<string, any>, forceRefresh = false): void {
    if (!this.ggApi || !this.gridConfig) return;

    const cfg = this.gridConfig;
    const cacheKey = cfg.cacheKey;

    if (!forceRefresh && cacheKey) {
      const cached = this.cacheService.get(cacheKey);
      if (cached) {
        cached.firstLoad = 0;
        this.processAndLoadData(cached);
        return;
      }
    }

    if (forceRefresh && cacheKey) {
      this.cacheService.clear(cacheKey);
    }

    if (this.loadSub) {
      this.loadSub.unsubscribe();
      this.loadSub = undefined;
    }

    const actionParam = cfg.actionCode
      ? `action_code=${cfg.actionCode}`
      : `action_id=${cfg.actionId}`;
    const url = `${environment.base_url}/loadGridJson?${actionParam}&grid_id=${cfg.gridId}`;
    const globalParams = this.globalService.global?.globalParams || {};
    const params = { ...globalParams, ...(cfg.extraParams || {}), ...(overrideParams || {}) };

    this.ggApi.showLoader?.();
    this.loadingStateChange.emit(true);

    this.loadSub = this.gridFactory.loadGridData(url, params)
      .pipe(take(1))
      .subscribe(
        (data: any) => {
          if (data?.callSuccess === '1') {
            this.processAndLoadData(data);
            if (cacheKey) {
              this.cacheService.set(cacheKey, data);
            }
          } else {
            console.warn('[AdminGrid] loadGridData failed:', data?.errorMessage);
            this.ggApi?.hideLoader?.();
          }
          this.loadingStateChange.emit(false);
        },
        (error: any) => {
          console.error('[AdminGrid] loadGridData error:', error);
          this.ggApi?.hideLoader?.();
          this.loadingStateChange.emit(false);
        },
      );
  }

  // ---------------------------------------------------------------------------
  // Data processing  (extracted from AdminUsersComponent / GridDirective)
  // ---------------------------------------------------------------------------

  private processAndLoadData(data: any): void {
    if (!this.ggApi) return;

    if (data.settings) {
      try {
        data.settings.batch_select_rules = data.settings.batch_select_rules
          ? (typeof data.settings.batch_select_rules === 'string'
            ? JSON.parse(data.settings.batch_select_rules) : data.settings.batch_select_rules)
          : '';
      } catch (_e) {
        data.settings.batch_select_rules = '';
      }

      // Parity with workspace `GridDirective.processAndLoadData`: the legacy
      // gizmoGrid plugin (`assets/lib/gizmo-grid/gizmoGrid.js`) reads these
      // two fields off `options.data.settings` to drive pivot UI, comment
      // dropdown defaults, and a few helper toolbar paths that also influence
      // header rendering. Workspace sets them unconditionally; admin grids
      // historically did not, leaving the values `undefined`. Mirror the
      // workspace defaults so admin grids exercise the same gizmoGrid code
      // paths as workspace screens.
      if (data.settings.hasPivot == null) {
        data.settings.hasPivot =
          data.settings.grid_type === 'import' || data.settings.grid_type === 'custom' ? 0 : 1;
      }
      if (data.settings.commentType == null) {
        data.settings.commentType = 'all';
      }
    }

    // Snapshot the user's column filter chips / sort / global-search input
    // BEFORE the firstLoad reset below wipes them. Mirrors the equivalent
    // fix in the core workspace `GridDirective` (which had to do this via
    // a transient `_preservedTableStateOnReload` property because the
    // singleton `TabGridLoaderService.finalize` overwrites
    // `activeScreen.data` before `processAndLoadData` runs). Admin grids
    // use `this.lastLoadedData` as their data carrier and only overwrite
    // it at the END of `processAndLoadData` (~line 421), so the previous
    // tableState is still intact at this point — no transient property
    // needed. Drill-down navigation does not exist for admin grids, so
    // the bypass branch from the core directive is intentionally omitted.
    const isFreshFirstLoad = data.firstLoad == null || data.firstLoad === 1;
    let priorFilterState: {
      colSearch: any[];
      sort: any;
      globalValue: any;
    } | null = null;
    if (isFreshFirstLoad && this.lastLoadedData?.tableState) {
      const prev = this.lastLoadedData.tableState;
      const hasColSearch = Array.isArray(prev.colSearch) && prev.colSearch.length > 0;
      const hasSort = prev.sort && !_.isEmpty(prev.sort);
      const hasGlobalSearch =
        prev.search?.globalValue != null && prev.search.globalValue !== '';
      if (hasColSearch || hasSort || hasGlobalSearch) {
        priorFilterState = {
          colSearch: hasColSearch ? _.cloneDeep(prev.colSearch) : [],
          sort: hasSort ? _.cloneDeep(prev.sort) : {},
          globalValue: hasGlobalSearch ? prev.search.globalValue : null,
        };
      }
    }

    if (data.firstLoad == null || data.firstLoad === 1) {
      data.batchSelect = [];
      data.selected = {
        color: 'green', select_type: null, row_id: null, col_id: null,
        obj_id: null, data_type: null, edit_type: null, validation: null,
        action: null, format: null, value: null, oldValue: null,
        rowData: null, editable: false, element: null,
      };
      data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
      data.cellData = {};
      data.vDragg = { draggerPositionY: 0, scrollPositionY: 0, startIndex: 0, endIndex: 0 };
      data.hDragg = { draggerPositionX: 0, scrollPositionX: 0, startIndex: 0, endIndex: 0, maxStartIndex: 0 };
    }
    if (data.firstLoad == null) {
      data.firstLoad = 1;
    }

    // Restore the user-applied filter chips / sort / global-search captured
    // before the reset block above. Without this restore, every refresh
    // (`refresh()` / `forceRefresh=true`) silently drops the footer chips
    // and resets sort order even though the underlying data has not
    // changed columns. Defensive guards mirror the core grid directive:
    //   - `validPredicateSet` drops chips whose column is missing from
    //     the new payload (would otherwise produce a "0 rows" state).
    //   - Sort is only restored when its predicate column still exists.
    //   - `normalizeColSearchTypes` coerces filter values to the new
    //     payload's column types, avoiding strict-equality mismatches in
    //     gizmoGrid's pipe().
    if (priorFilterState) {
      if (!data.tableState) {
        data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
      }
      const validPredicateSet = new Set(
        (data.header || []).map((h: any) => String(h?.map))
      );
      const survivingColSearch = priorFilterState.colSearch.filter(
        (f: any) => f?.predicate && validPredicateSet.has(String(f.predicate))
      );
      if (survivingColSearch.length) {
        data.tableState.colSearch = this.normalizeColSearchTypes(survivingColSearch, data);
      }
      if (
        !_.isEmpty(priorFilterState.sort) &&
        priorFilterState.sort.predicate &&
        validPredicateSet.has(String(priorFilterState.sort.predicate))
      ) {
        data.tableState.sort = priorFilterState.sort;
      }
      if (priorFilterState.globalValue != null && priorFilterState.globalValue !== '') {
        data.tableState.search = {
          ...(data.tableState.search || {}),
          globalValue: priorFilterState.globalValue,
        };
      }
    }

    this.processTemplates(data);

    const ggData: any = {};
    ggData.headerArray = data.header || [];
    ggData.dataArr = data.data || [];
    ggData.settings = data.settings;
    ggData.tableState = data.tableState;
    ggData.cellData = data.cellData;
    ggData.selected = data.selected;
    ggData.vDragg = data.vDragg;
    ggData.hDragg = data.hDragg;
    ggData.batchSelect = data.batchSelect || [];
    ggData.columnTemplates = data.columnTemplates || [];
    ggData.actionTemplates = data.actionTemplates || [];
    ggData.pivotActionTemplates = [];
    ggData.firstLoad = data.firstLoad;
    ggData.pivotGroup = data.pivotGroup || {};
    ggData.messageObject = [];
    ggData.userAccessLevel = this.appService.USER_SETTINGS?.user?.system_access_level || 1;
    ggData.sso_id = this.appService.USER_SETTINGS?.user?.sso_id || '';

    for (let i = 0; i < ggData.headerArray.length; i++) {
      ggData.headerArray[i].object_id = i.toString();
    }

    this.ggApi.hideOverLay?.();
    this.ggApi.loadData({ data: ggData });

    if (!ggData.dataArr || ggData.dataArr.length === 0) {
      this.ggApi.hideLoader?.();
      this.ggApi?.gridRefreshPipe?.([]);

      const $grid = $(this.el.nativeElement);
      $grid.find('.gg-filter-container').empty();
      $grid.find('.gg-alert').remove();

      this.gtwSnackbarService.openSnackBar(
        'No data found with this filter criteria.',
        'gtw-snackbar--yellow',
      );
    }

    this.lastLoadedData = data;
    this.adminScreenContext.setGridData(data);
    this.gridDataLoaded.emit(data);
  }

  /**
   * Coerce colSearch `value[]` entries to the data type of the new payload's
   * actual row data. The backend is occasionally inconsistent — e.g. a
   * column may arrive as string in one response and as number in the next
   * after columns are recomputed. gizmoGrid's `pipe()` compares with strict
   * equality (`_.findByValues`), so a type mismatch silently filters the
   * grid down to zero rows. Mirrors `GridDirective.normalizeColSearchTypes`.
   */
  private normalizeColSearchTypes(filters: any[], data: any): any[] {
    const sampleRow = data?.data?.[0];
    const cloned = _.cloneDeep(filters);
    if (!sampleRow) return cloned;
    return cloned.map((f: any) => {
      if (!f || !Array.isArray(f.value) || f.value.length === 0) return f;
      const refVal = sampleRow[f.predicate];
      if (refVal == null) return f;
      const targetType = typeof refVal;
      if (targetType !== 'number' && targetType !== 'string') return f;
      const needsCoerce = f.value.some((v: any) => typeof v !== targetType);
      if (!needsCoerce) return f;
      f.value = f.value.map((v: any) => {
        if (targetType === 'number') {
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        }
        return String(v);
      });
      return f;
    });
  }

  private processTemplates(data: any): void {
    if (!data.header) return;

    data.header.forEach((col: any) => {
      if (col.edit_rules && typeof col.edit_rules === 'string') {
        try { col.edit_rules = JSON.parse(col.edit_rules); } catch (_e) { /* ignore */ }
      }
    });

    if (data.columnTemplates) {
      data.columnTemplates.forEach((tpl: any) => {
        if (typeof tpl.columns === 'string') {
          try { tpl.columns = JSON.parse(tpl.columns); } catch (_e) { tpl.columns = ''; }
        }
      });
    } else {
      data.columnTemplates = [];
    }

    if (data.header?.length) {
      const templateArr = data.header.map((h: any) => ({
        width: h.width, display: h.display, map: h.map,
      }));
      const defaultTemplate = {
        name: 'Default Columns', template_key: 0, system: 1,
        default_template: 0, columns: templateArr,
      };
      if (!data.columnTemplates.some((t: any) => t.template_key === 0)) {
        data.columnTemplates.unshift(defaultTemplate);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Callback routing
  // ---------------------------------------------------------------------------

  private onGridCallback(object: any): void {
    const event: AdminGridCallbackEvent = {
      callName: object.callName,
      data: object.data,
      type: object.type,
    };

    if (object.type === 'columnTemplate') {
      this.gridSaveFactory.colTemplatesUpdate(
        this.gridConfig.gridId,
        object.callName,
        object.data,
      );
    }

    this.gridCallback.emit(event);
  }

  // ---------------------------------------------------------------------------
  // Export to Excel (parity with GridDirective / legacy admin templates)
  // ---------------------------------------------------------------------------

  private getGridLiveData(): any {
    return this.adminScreenContext.gridData || this.lastLoadedData;
  }

  /** Legacy gridController.isFiltered() — colSearch or selected grid action. */
  private isFilteredExportAvailable(): boolean {
    const data = this.getGridLiveData();
    const colSearch = this.resolveExportColSearch(data);
    const selectedAction = _.filter(data?.actionTemplates || [], { selectedAction: true });
    return colSearch.length > 0 || selectedAction.length > 0;
  }

  /**
   * Resolve live column filters for export. gizmoGrid owns the authoritative
   * tableState while the grid is interactive.
   */
  private resolveExportColSearch(gridData: any): any[] {
    const liveColSearch = this.ggApi?.getTableState?.()?.colSearch;
    if (Array.isArray(liveColSearch) && liveColSearch.length > 0) {
      return liveColSearch;
    }

    const selectedActions = _.filter(gridData?.actionTemplates || [], { selectedAction: true });
    for (const action of selectedActions) {
      const rawGridData = action?.grid_data ?? action?.gridData;
      if (!rawGridData) {
        continue;
      }
      try {
        const parsed = typeof rawGridData === 'string' ? JSON.parse(rawGridData) : rawGridData;
        const savedColSearch = parsed?.tableState?.colSearch;
        if (Array.isArray(savedColSearch) && savedColSearch.length > 0) {
          return savedColSearch;
        }
      } catch {
        // Ignore malformed saved grid action payloads.
      }
    }

    const screenColSearch = gridData?.tableState?.colSearch;
    return Array.isArray(screenColSearch) ? screenColSearch : [];
  }

  private pickFirstNonEmpty(...vals: any[]): string {
    for (const val of vals) {
      if (val === undefined || val === null) continue;
      const text = String(val).trim();
      if (text !== '' && text !== 'undefined' && text !== 'null') {
        return text;
      }
    }
    return '';
  }

  /** Accept numeric screen keys; reject route-path style values (e.g. admin/users-list). */
  private looksLikeExportScreenKey(value: any): boolean {
    const text = String(value ?? '').trim();
    if (!text || text === 'undefined' || text === 'null') {
      return false;
    }
    if (text.includes('/') || text.includes('\\')) {
      return false;
    }
    return /^\d+$/.test(text);
  }

  /**
   * Resolve export `screen_key` when available. Admin has no workspace
   * activeScreen; an empty result is still sent so the backend can decide
   * (action_id + grid_id may suffice). Priority: exportScreenKey → settings →
   * extraParams.
   */
  private resolveExportScreenKey(cfg: AdminGridConfig, settings: any): string {
    const fromSettings = this.pickFirstNonEmpty(
      settings?.screen_key,
      settings?.SCREEN_KEY,
      settings?.screen_key_id,
      settings?.screenKey,
    );
    const extraScreenKey = cfg.extraParams?.screen_key;
    const fromExtra = this.looksLikeExportScreenKey(extraScreenKey) ? String(extraScreenKey) : '';
    return this.pickFirstNonEmpty(cfg.exportScreenKey, fromSettings, fromExtra);
  }

  private buildExportGridSettings(settings: any): string {
    if (settings == null || (_.isObject(settings) && _.isEmpty(settings))) {
      return 'undefined';
    }
    return _.isString(settings) ? settings : JSON.stringify(settings);
  }

  private syncFilteredExportVisibility(): void {
    const $grid = $(this.el.nativeElement);
    if (!$grid?.length) {
      return;
    }
    const show = this.isFilteredExportAvailable();
    $grid
      .find('.gg-export-action[action="filtered-export"]')
      .css('display', show ? '' : 'none');
  }

  private showExportSpinner(): void {
    const $grid = $(this.el.nativeElement);
    if (!$grid?.length) return;
    const toolsExport = $grid.find('.tools-export');
    toolsExport.addClass('gg-export-to-excel');
    if (!toolsExport.find('i.fa-spinner').length) {
      toolsExport.append('<i class="fa fa-spinner fa-spin"></i>');
    }
  }

  private hideExportSpinner(): void {
    const $grid = $(this.el.nativeElement);
    if (!$grid?.length) return;
    const toolsExport = $grid.find('.tools-export');
    toolsExport.removeClass('gg-export-to-excel');
    toolsExport.find('i.fa-spinner').remove();
  }

  private buildExportParams(): {
    jsonData: any;
    actionId: string;
    actionCode: string;
    gridId: string;
    ssoId: string;
    screenKey: string;
  } | null {
    const cfg = this.gridConfig;
    if (!cfg) {
      return null;
    }

    const settings = this.lastLoadedData?.settings || {};

    let actionId = '';
    if (cfg.exportActionId !== undefined && cfg.exportActionId !== null && String(cfg.exportActionId).trim() !== '') {
      actionId = String(cfg.exportActionId);
    }
    if (!actionId && cfg.actionId !== undefined && cfg.actionId !== null && String(cfg.actionId).trim() !== '') {
      actionId = String(cfg.actionId);
    }
    if (!actionId) {
      const fallback = settings.action_id ?? settings.action_key ?? settings.actionId;
      if (fallback !== undefined && fallback !== null && String(fallback).trim() !== '') {
        actionId = String(fallback);
      }
    }

    const actionCode = actionId ? '' : String(cfg.actionCode || '').trim();

    let gridId = this.pickFirstNonEmpty(cfg.exportGridId, cfg.gridId);
    if (!gridId) {
      gridId = this.pickFirstNonEmpty(settings.grid_id, settings.gridId);
    }

    const globalParams = this.globalService.global?.globalParams || {};
    const merged = { ...globalParams, ...(cfg.extraParams || {}) };

    const jsonData: any = { isFileDownloadRequest: 'Y' };
    Object.keys(merged).forEach((k: string) => {
      const v = merged[k];
      if (v != null && v !== '' && v !== 'undefined' && v !== 'null') {
        jsonData[k] = String(v);
      }
    });

    const keysToExcludeDisplay = [
      'tax_year', 'screen_key', 'jcd_key', 'jcd_ta_key',
      'activity_key', 'screen_type', 'is_locked', 'isFileDownloadRequest',
    ];
    Object.keys(jsonData).forEach((k: string) => {
      if (!keysToExcludeDisplay.includes(k) && !k.startsWith('display_')) {
        jsonData[`display_${k}`] = jsonData[k];
      }
    });

    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const screenKey = this.resolveExportScreenKey(cfg, settings);

    return { jsonData, actionId, actionCode, gridId, ssoId, screenKey };
  }

  private handleExportResponse(data: any, ssoId: string): void {
    const baseUrl = environment.base_url;
    if (data?.ERROR) {
      this.gtwSnackbarService.openSnackBar(data.ERROR, 'gtw-snackbar--red');
      this.exportDone();
      return;
    }
    if (data?.callSuccess != 1 && data?.callSuccess !== '1') {
      this.gtwSnackbarService.openSnackBar(
        'Data can not be exported, please contact System administrator',
        'gtw-snackbar--red',
      );
      this.exportDone();
      return;
    }

    this.http.get<any>(`${baseUrl}/getAPIAuthToken`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (tokenRes: any) => {
        if (tokenRes?.apiToken) {
          const clientKey = sessionStorage.getItem('client_key') || '';
          const downloadUrl = `${baseUrl}/download?file_name=${encodeURIComponent(data.file_name)}`
            + `&client_key=${encodeURIComponent(clientKey)}`
            + `&apiToken=${encodeURIComponent(tokenRes.apiToken)}`
            + `&georaclehrid=${encodeURIComponent(ssoId)}`;
          window.open(downloadUrl, '_blank');
        } else {
          this.gtwSnackbarService.openSnackBar(
            'Failed to retrieve auth token for download',
            'gtw-snackbar--red',
          );
        }
        this.exportDone();
      },
      () => {
        this.gtwSnackbarService.openSnackBar(
          'Error retrieving auth token. Please try again.',
          'gtw-snackbar--red',
        );
        this.exportDone();
      },
    );
  }

  private exportDone(): void {
    this.hideExportSpinner();
  }

  private exportToExcel(): void {
    const params = this.buildExportParams();
    if (!params) {
      return;
    }

    this.showExportSpinner();
    const { jsonData, actionId, actionCode, gridId, ssoId, screenKey } = params;

    if ((!actionId && !actionCode) || !gridId) {
      this.gtwSnackbarService.openSnackBar(
        'Export is unavailable: grid metadata is missing (action/grid id).',
        'gtw-snackbar--red',
      );
      this.exportDone();
      return;
    }

    const baseUrl = environment.base_url;
    const actionParam = actionId
      ? `action_id=${encodeURIComponent(actionId)}`
      : `action_code=${encodeURIComponent(actionCode)}`;
    const exportUrl = `${baseUrl}/exportToExcel?${actionParam}`
      + `&grid_id=${encodeURIComponent(gridId)}`
      + `&sso_id=${encodeURIComponent(ssoId)}`
      + `&screen_key=${encodeURIComponent(screenKey)}`;
    const exportData = this.ggApi?.getExportData?.();
    const headerArray: any[] = exportData?.headerArray || this.lastLoadedData?.header || [];
    const normalizedHeaders = headerArray.map((item: any) => ({
      object_id: item.object_id,
      label: item.label ?? null,
      data_type: item.data_type ?? null,
      format: item.format ?? null,
      map: item.map,
      width: item.width,
    }));

    const jsonObj = JSON.stringify(jsonData);
    const gridSettings = this.buildExportGridSettings(exportData?.settings);

    const body =
      `jsonObj=${encodeURIComponent(jsonObj)}` +
      `&headerArray=${encodeURIComponent(JSON.stringify(normalizedHeaders))}` +
      `&gridSettings=${encodeURIComponent(gridSettings)}`;

    this.http.post<any>(exportUrl, body, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
      withCredentials: true,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => this.handleExportResponse(data, ssoId),
      () => {
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while exporting. Please try again.',
          'gtw-snackbar--red',
        );
        this.exportDone();
      },
    );
  }

  private exportToExcelFiltered(): void {
    if (!this.isFilteredExportAvailable()) {
      return;
    }

    const params = this.buildExportParams();
    if (!params) {
      return;
    }

    this.showExportSpinner();
    const { jsonData, actionId, actionCode, gridId, ssoId, screenKey } = params;

    if ((!actionId && !actionCode) || !gridId) {
      this.gtwSnackbarService.openSnackBar(
        'Export is unavailable: grid metadata is missing (action/grid id).',
        'gtw-snackbar--red',
      );
      this.exportDone();
      return;
    }

    const baseUrl = environment.base_url;
    const gridData = this.getGridLiveData();
    const colSearch = this.resolveExportColSearch(gridData);
    colSearch.forEach((col: any) => {
      if (col.predicate && col.value != null) {
        jsonData[`colFilter_${col.predicate}`] = col.value.toString();
      }
    });

    const liveTableState = this.ggApi?.getTableState?.();
    if (liveTableState && gridData) {
      gridData.tableState = liveTableState;
    }

    const exportData = this.ggApi?.getExportData?.();
    const headerArray: any[] = exportData?.headerArray || this.lastLoadedData?.header || [];
    const dataArr: any[] = exportData?.dataArr || [];
    const gridSettings = this.buildExportGridSettings(exportData?.settings);

    const headArra: any[] = [];
    const filteredMap: string[] = [];
    headerArray.forEach((item: any) => {
      headArra.push({
        object_id: item.object_id,
        label: item.label,
        data_type: item.data_type,
        format: item.format,
        map: item.map,
        width: item.width,
      });
      filteredMap.push(item.map);
    });

    const jsonObj = JSON.stringify(jsonData);
    const actionParam = actionId
      ? `action_id=${encodeURIComponent(actionId)}`
      : `action_code=${encodeURIComponent(actionCode)}`;
    let exportUrl = `${baseUrl}/exportToExcel?${actionParam}`
      + `&grid_id=${encodeURIComponent(gridId)}`
      + `&sso_id=${encodeURIComponent(ssoId)}`
      + `&screen_key=${encodeURIComponent(screenKey)}`;

    if (dataArr.length > 50000) {
      exportUrl += '&is_compressed=false&filtered_export=false';
      const body = `jsonObj=${encodeURIComponent(jsonObj)}`
        + `&headerArray=${encodeURIComponent(JSON.stringify(headArra))}`
        + `&gridSettings=${encodeURIComponent(gridSettings)}`;

      this.http.post<any>(exportUrl, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
        withCredentials: true,
      }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => this.handleExportResponse(data, ssoId),
        () => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while exporting. Please try again.',
            'gtw-snackbar--red',
          );
          this.exportDone();
        },
      );
    } else {
      exportUrl += '&is_compressed=true&filtered_export=true';

      const gridObject: any = { header: [filteredMap], data: [] as any[] };
      dataArr.forEach((obj: any) => {
        const row: any[] = [];
        filteredMap.forEach((map: string) => {
          row.push(obj[map] === undefined ? null : obj[map]);
        });
        gridObject.data.push(row);
      });

      const blob = new Blob([JSON.stringify(gridObject)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const fd = new FormData();
      fd.append('file', blob);
      fd.append('jsonObj', jsonObj);
      fd.append('headerArray', JSON.stringify(headArra));
      fd.append('gridSettings', gridSettings);

      this.http.post<any>(exportUrl, fd, { withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => this.handleExportResponse(data, ssoId),
        () => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while exporting. Please try again.',
            'gtw-snackbar--red',
          );
          this.exportDone();
        },
      );
    }
  }
}
